import { useCallback } from 'react'

import { useGuardedModalDismiss } from 'src/hooks/use-guarded-modal-dismiss'

import Text from '../../../components/Typography/Text'

import Modal from './Modal'

type Props = {
  cancelText?: string
  centered?: boolean
  children: React.ReactNode
  confirmText?: string
  confirmTestId?: string
  isDestructive?: boolean
  isLoading?: boolean
  onCancel: () => void
  onConfirm: (() => void) | (() => Promise<unknown>)
  /**
   * Side effects that run after Radix dismisses the dialog (refetch, toasts, etc.).
   * Keep mutations in `onConfirm` only — refetch here so the modal can close first.
   */
  onClosed?: () => void | Promise<unknown>
  /**
   * Customize which element receives focus when the modal closes; by default it is the element that triggered the modal, but you will need to set this if the trigger no longer exists (e.g. a menu item)
   */
  onCloseFocusRef?: React.RefObject<HTMLElement>
  /**
   * Visible modal header and dialog accessible name. Use Title Case (e.g. "Delete Holiday",
   * "Discard Changes"), consistent with other `Modal.Header` titles. Display is normalized
   * in `Modal.Header` when sentence case is passed by mistake.
   */
  title: string
}
const ConfirmationModal = ({
  cancelText,
  centered,
  children,
  confirmText,
  confirmTestId,
  isDestructive,
  isLoading,
  onCancel,
  onConfirm,
  onClosed,
  onCloseFocusRef,
  title,
}: Props) => {
  const { onCloseIntent, onBindDismiss, onModalClosed, markConfirmComplete } =
    useGuardedModalDismiss({
      shouldBlock: isLoading ?? false,
      onClose: () => {
        onCancel()
        void onClosed?.()
      },
    })

  const handleConfirm = useCallback(async () => {
    await onConfirm()
    markConfirmComplete()
  }, [markConfirmComplete, onConfirm])

  return (
    <Modal
      onClose={onModalClosed}
      onCloseIntent={onCloseIntent}
      onBindDismiss={onBindDismiss}
      onCloseFocusRef={onCloseFocusRef}
      centered={centered}
      preventOverlayClose={isLoading}
    >
      <Modal.Header>{title}</Modal.Header>
      <Modal.Content>
        {typeof children === 'string' ? <Text>{children}</Text> : children}
      </Modal.Content>
      <Modal.Footer
        cancelText={cancelText}
        confirmText={confirmText}
        confirmTestId={confirmTestId}
        isDestructive={isDestructive}
        onConfirm={handleConfirm}
        isLoading={isLoading}
      />
    </Modal>
  )
}

export default React.memo(ConfirmationModal)
